import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StorageService } from '../../../../core/services/storage.service';
import { CommonModule, Location } from '@angular/common';
import Order from '../../../../core/models/order';
import { Constants } from '../../../../core/utils/constants';
import TicketType from '../../../../core/models/ticket.type';
import { UtilService } from '../../../../core/services/util.service';
import { EventService } from '../../services/event.service';
import { CouponService } from '../../services/coupon.service';
import PaymentInfo from 'src/app/core/models/payment.info';
import { LoadingComponent } from '../../../../core/components/loading/loading.component';
import { FormControl, Validators } from '@angular/forms';
import { firstValueFrom, lastValueFrom, Subject, Subscription } from 'rxjs';
import { ReCaptchaV3Service, RecaptchaComponent } from 'ng-recaptcha';
import Swal from 'sweetalert2';
import { ThreeDSChallengeComponent } from './three-dschallenge/three-dschallenge.component';
import { OrderService } from '../../../order/services/order.service';
import Coupon from '../../../../core/models/coupon';
import CouponType from '../../../../core/enums/couponType.enum';
import { getOrderCreationErrorMessage } from 'src/app/core/utils/getOrderCreationErrorMessage';
import { OrderCreationError } from 'src/app/core/enums/orderCreationError.enum';
import {
  ErrorInformation,
  ThreeDSCybersourceService,
} from 'src/app/services/three-dscybersource.service';
import { OrderPrices } from 'src/app/core/types/OrderPrices.type';
import { TixGateway } from 'src/app/core/enums/tixGateway.enum';
import {
  CheckoutAzulResponse,
  CheckoutResponse,
  CheckoutSessionService,
  CheckoutTixErrorResponse,
  NonEmptyDiscountTokens,
} from '../../services/checkout-session.service';
import { Product } from 'src/app/core/types/product.type';
import EventModel from 'src/app/core/models/event';
import { CybersourceThreeDSStatus } from 'src/app/core/enums/cybersourceThreeDSStatus.enum';
import { MetaPixelService } from '../../services/meta-pixel.service';
import { MetaPixelEvent } from 'src/app/core/enums/metaPixelEvent.enum';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { GtmEvent } from 'src/app/core/enums/gtmEvent.enum';
import { getPlaces } from 'src/app/core/functions/getPlaces';
import { GooglePayResponse } from 'src/app/core/types/googlePayResponse.type';
import { LoadPaymentDataInfo } from 'src/app/core/components/pay-with-wallet/pay-with-wallet.component';
import { COUNTRIES, Country } from './smart-input/smart-input.types';

export type ExplicitCouponType<T = CouponType> = Omit<Coupon, 'type'> & {
  type: T;
};
export type ExplicitCouponsType<T = CouponType> = ExplicitCouponType<T>[];

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  @ViewChild(LoadingComponent) loadingComponent!: LoadingComponent;
  @ViewChild('threeChallengeModal')
  threeChallengeModal!: ThreeDSChallengeComponent;
  @ViewChild(RecaptchaComponent) challengeBox?: RecaptchaComponent;
  orderPrice: OrderPrices = {
    fees: {
      insuranceFee: 0,
      serviceFee: 0,
    },
    discounted: 0,
    orderAmountWithDiscount: 0,
    checkoutSessionTimestamp: 0,
    gateway: TixGateway.AZUL,
    orderAmount: 0,
    securedTotal: 0,
    discount: 0,
    total: 0,
  };

  public paymentStatus = new Subject<{ authorized: boolean }>();

  selectedTicketTypes: TicketType[] = [];
  selectedSeats: Record<number, string[]> = {};
  promoterSlug = '';
  products: Product[] = [];
  event!: EventModel;

  showSummary = false;
  emailInput = new FormControl('', [Validators.required, Validators.email]);
  emailInputSubscription: Subscription | undefined;
  dates: string[] = [];
  gateway: string = '';

  couponError = '';

  public order: Order | null = null;
  public couponCode: string = '';
  public couponButtonLabel: string = 'Aplicar';
  public error:
    | {
        code: string;
        error: string;
        msg: string;
        iso_code: string;
        tix_error: OrderCreationError;
      }
    | string
    | { recommendation: string; response: string; code: string }
    | { code: string; reason: string; message: string }
    | undefined = undefined;
  public insuranceFee: number = 0;

  public checkoutTimestamp: number = 0;

  public timeoutMinutes: number = -1;
  public timeoutSeconds: number = -1;

  public codeCoupons: ExplicitCouponsType<CouponType.Code> = [];
  public cardCoupon?: ExplicitCouponType<CouponType.CreditCard>;
  public discountTokens: string[] = [];

  get places() {
    const _place = this.event?.place;
    if (!_place) return ['cargando...'];

    return getPlaces(this.event, this.selectedTicketTypes);
  }

  // Mobile order summary expand/collapse functionality
  isOrderSummaryExpanded = false;

  getTotalTicketCount(): number {
    return this.products.reduce((total, product) => total + product.qty, 0);
  }

  getTicketTypeSummary(): string {
    if (this.selectedTicketTypes.length === 0) return '0 tickets';

    if (this.selectedTicketTypes.length === 1) {
      const ticketType = this.selectedTicketTypes[0];
      const qty = this.getProductByTicketType(ticketType.id).qty;
      return `${ticketType.name} x ${qty}`;
    }

    // Multiple ticket types
    const summary = this.selectedTicketTypes
      .map((ticketType) => {
        const qty = this.getProductByTicketType(ticketType.id).qty;
        return `${ticketType.name} x ${qty}`;
      })
      .join(', ');

    return summary;
  }

  toggleOrderSummary(): void {
    this.isOrderSummaryExpanded = !this.isOrderSummaryExpanded;
  }

  private countDownInterval: NodeJS.Timer | null = null;

  public buttonStatus = {
    coupon: true,
    checkout: true,
  };

  // The backend asks for a challenge on a low reCAPTCHA v3 score, or when the card's
  // BIN is flagged by decline velocity. Solve the checkbox, retry the same checkout.
  public challengeRequired = false;
  public challengeToken: string | null = null;

  public buyerData = {
    name: '',
    email: '',
    coupon: null,
  };

  public paymentInfo: PaymentInfo = {
    card: '',
    cardType: '',
    cvc: '',
    expDate: '',
    name: '',
    secured: true,
  };

  // Card brand detected from the card number typed by the user
  public detectedCardBrand: 'visa' | 'mastercard' | 'amex' | 'discover' | null =
    null;
  public isExpirationDateExpired: boolean = false;

  // Validation state tracking
  public validationTouched = {
    name: false,
    email: false,
    card: false,
    expDate: false,
    cvc: false,
    cardholderName: false,
  };

  private sessionToken: string = '';
  private googlePayToken: string = '';
  private applePayToken: string = '';

  eventFree = false;

  getValidTicketTypes(coupon: Coupon) {
    if (!coupon.ticket_types?.length) return '';

    const names = coupon.ticket_types.map((tt) => tt.name).join(', ');

    return `PARA TICKETS: ${names}`;
  }

  get isEmailInvalid() {
    const result = this.emailInput.errors
      ? this.emailInput.errors['email']
      : false;

    return result;
  }

  get ticketsReleasedEvents() {
    return 'on_hold_tickets_released';
  }

  get startTimerEvent() {
    return 'start_checkout_timer';
  }

  get codeCouponsDiscount() {
    return this.orderService.codeCouponsDiscount(this.order);
  }

  get couponsNames() {
    return this.codeCoupons.map((c) => c.code).join(', ');
  }

  threeDSForm: SafeHtml = '';

  domains = ['@gmail.com', '@hotmail.com', '@icloud.com', '@yahoo.com'];

  emailDomainsSuggestions: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private readonly metaPixelService: MetaPixelService,
    private readonly gtmService: GoogleTagManagerService,
    private storageService: StorageService,
    private checkoutSessionService: CheckoutSessionService,
    private router: Router,
    private location: Location,
    private utilService: UtilService,
    private eventService: EventService,
    private couponService: CouponService,
    private orderService: OrderService,
    private sanitizer: DomSanitizer,
    private threedsCybersourceService: ThreeDSCybersourceService,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {}

  async ngOnInit(): Promise<void> {
    this.emailInputSubscription = this.emailInput.valueChanges.subscribe(
      (value) => {
        this.buyerData.email = value!;
        if (value?.includes('@')) return;

        this.emailDomainsSuggestions = this.domains.map(
          (domain) => value + domain
        );
      }
    );

    try {
      const retrieved = this.loadState();

      if (!retrieved) {
        await this.goBackToEvent();
        return;
      }

      if (this.event?.pixelId) {
        this.metaPixelService.insertScript(
          this.event.pixelId,
          MetaPixelEvent.INITIATE_CHECKOUT
        );
      }
      if (this.event?.gtmId) {
        this.gtmService.insertScript(this.event.gtmId);
        this.gtmService.pushEvent(GtmEvent.BEGIN_CHECKOUT, {
          item_id: this.event.id,
          item_name: this.event.name,
        });
      }

      this.addStartOnDates(this.selectedTicketTypes);

      await this.applyPrices();

      const _timestamp = this.storageService.get(
        Constants.Keys.CheckoutTimeout,
        Constants.StorageType.Session
      );

      if (_timestamp !== null) {
        this.checkoutTimestamp = parseInt(_timestamp);
      } else {
        this.checkoutTimestamp = 0;
      }

      this._startTimer(true);
    } catch (err: any) {
      console.log(err);
      this.goBackToEvent();
    }
  }

  ngOnDestroy() {
    this.emailInputSubscription?.unsubscribe();
    this.metaPixelService.removeScript();
    this.gtmService.removeScript();
    this._clearSession();
  }

  getProductByTicketType(ticketTypeId: number) {
    return this.products.find((p) => p.ticket_type_id === ticketTypeId)!;
  }

  getDiscountedByCardDiscount() {
    if (!this.cardCoupon) return 0;

    const cardCouponDiscount = this.cardCoupon.discount || 0;
    const orderAmount = this.orderPrice.orderAmount;

    return orderAmount * (cardCouponDiscount / 100);
  }

  private loadState(): boolean {
    const sessionData = sessionStorage.getItem(Constants.Keys.CheckoutSession);
    if (!sessionData) {
      return false;
    }
    const { sessionToken, products, selectedTicketTypes, event, promoterSlug } =
      JSON.parse(sessionData);
    this.sessionToken = sessionToken;
    this.products = products;

    console.log(products);
    this.selectedTicketTypes = selectedTicketTypes;
    this.setSelectedSeats();

    this.event = event;
    this.promoterSlug = promoterSlug;
    return true;
  }

  private setSelectedSeats() {
    this.products.forEach((p, index) => {
      if (!p.seats?.length) return;
      const tt = this.selectedTicketTypes.find(
        (t) => t.id === p.ticket_type_id
      );
      if (!this.selectedSeats[p.ticket_type_id])
        this.selectedSeats[p.ticket_type_id] = [];

      p.seats.forEach((s) => {
        const rowTracker = tt?.rows_trackers.find(
          (rt) => rt.id === s.tracker_id
        );
        if (!rowTracker) return;

        const seatIndex = s.preview_seat_index || s.seat_index;
        let label = `${rowTracker.prefix}${seatIndex}`;
        if (tt?.block_id) {
          label = label.replace(/\w+_/, ``);
        }
        this.selectedSeats[p.ticket_type_id].push(label);
      });
    });
  }

  private _clearSession(pending = true): void {
    if (this.countDownInterval != null) {
      if (pending)
        this.utilService
          .getSocket()
          .emit('release_hold_tickets', this.order?.ticketIds ?? []);
      this.utilService.getSocket().removeListener(this.ticketsReleasedEvents);
      this.utilService.getSocket().removeListener(this.startTimerEvent);

      clearInterval(this.countDownInterval as any);
      this.countDownInterval = null;
    }

    if (this.sessionToken) {
      this.checkoutSessionService.endSession(this.sessionToken);
    }

    this.order = null;
    sessionStorage.removeItem(Constants.Keys.CheckoutSession);
    this.storageService.remove(Constants.Keys.EventOrder);
    this.storageService.remove(
      Constants.Keys.CheckoutTimeout,
      Constants.StorageType.Session
    );
    this.storageService.remove(
      Constants.Keys.CheckoutTimeoutRemainingSeconds,
      Constants.StorageType.Session
    );
    this.storageService.remove(
      Constants.Keys.CheckoutTimeoutTicketHeld,
      Constants.StorageType.Session
    );
  }

  back(): void {
    this._clearSession();
    this.location.back();
  }

  applyInsuranceFee(): void {
    this.paymentInfo.secured = true;
    // Restore original insurance fee when applying insurance
    this.orderPrice.fees.insuranceFee = this.insuranceFee;
  }

  removeInsuranceFee(): void {
    this.paymentInfo.secured = false;
    // Set insurance fee to 0 when removing insurance
    this.orderPrice.fees.insuranceFee = 0;
  }

  calcTotal(): void {
    if (this.order) {
      const { subTotal, insuranceFee, serviceFee } = this.order;

      let cardDiscount = this.cardCoupon
        ? this.order.subTotal * (this.cardCoupon.discount / 100)
        : 0;

      if (this.cardCoupon) {
        const result = this.order.coupons?.find(
          (c) => c.id == this.cardCoupon?.id
        );
        result!.discounted = cardDiscount;
      }

      const discountedSubTotal =
        subTotal - this.codeCouponsDiscount - cardDiscount;

      this.order.discounted = this.codeCouponsDiscount - cardDiscount;
      this.order.totalToPay = discountedSubTotal + insuranceFee + serviceFee;
    }
  }

  getEventImageUrl(): string {
    return this.utilService.getAbsoluteUrl(this.event.coverUri ?? '');
  }

  getTotalTickets(): number {
    return this.products.reduce((prev, ac) => (prev += ac.qty), 0);
  }

  hasSelectedSeats(): boolean {
    return Object.values(this.selectedSeats).some(
      (seats) => seats && seats.length > 0
    );
  }

  applyCoupon(): void {
    if (!this.couponCode) return;

    this.buttonStatus.coupon = false;
    this.couponButtonLabel = '...';
    this.couponService
      .find(
        this.couponCode,
        this.event.id,
        this.selectedTicketTypes.map((t) => t.id)
      )
      .subscribe((_coupon) => {
        this.buttonStatus.coupon = true;
        this.couponButtonLabel = 'Aplicar';
        if (!_coupon) return;

        this.couponError = '';
        if (typeof _coupon === 'string') {
          const couponError = Constants.CouponErrors[_coupon];
          this.couponError = couponError || 'Cupón inválido';
          return;
        }
        if (_coupon.type !== CouponType.Code) return;

        this.codeCoupons.push(_coupon as ExplicitCouponType<CouponType.Code>);
        this.applyPrices(_coupon.discountToken);
      });
  }

  getErrorMessage(tixError?: OrderCreationError, isoCode?: string) {
    if (tixError) {
      return getOrderCreationErrorMessage(tixError);
    }

    let response;
    let recommendation;

    if (!isoCode) {
      isoCode = '-1';
    }

    if (Constants.TransactionErrors[isoCode]) {
      response = Constants.TransactionErrors[isoCode].response;
      recommendation = Constants.TransactionErrors[isoCode].recommendation;
    } else {
      response = Constants.TransactionErrors['-1'].response;
      recommendation = Constants.TransactionErrors['-1'].recommendation;
    }

    return {
      code: isoCode,
      response: `(${isoCode}) ${response}`,
      recommendation: recommendation.toUpperCase(),
    };
  }

  paymentError(
    error:
      | CheckoutTixErrorResponse
      | string
      | ErrorInformation
      | { response: string; recommendation: string }
  ) {
    this.buttonStatus.checkout = true;
    this.paymentStatus.next({ authorized: false });

    if (typeof error === 'object' && 'recommendation' in error) {
      const { recommendation, response } = error;
      this._hideLoadingDialog(() => {
        Swal.fire({
          title: `Declinada`,
          html: `
          <div>
            <p class="mb-4">${response}</p>
            <p>
             ${recommendation}
            </p>
          </div>`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      });
      return;
    }

    this.error = error;

    let tixError: OrderCreationError | undefined;
    let isoCode: string | undefined;

    if (typeof error === 'object' && 'tix_error' in error) {
      tixError = error.tix_error;
    }

    this._hideLoadingDialog(() => {
      if (!error) {
        error = '';
      }

      if (
        typeof error === 'object' &&
        'iso_code' in error &&
        !Constants.TransactionErrors[error!.iso_code]
      ) {
        error = error?.iso_code;
      }

      if (typeof error === 'object' && 'iso_code' in error) {
        isoCode = error?.iso_code || error.code;
      }

      let msg: any;
      if (typeof error === 'object' && 'recommendation' in error) {
        msg = error;
      } else {
        msg = this.getErrorMessage(tixError, isoCode);
      }

      //TODO: Define a better way to notify client about any error regarding order payment
      Swal.fire({
        title: `Declinada`,
        html:
          typeof error == 'string'
            ? error
            : `<div>
                <p class="mb-4">${msg?.code}</p>
                <p class="mb-4">${msg.response}</p>
                <p>
                 ${!tixError ? msg.recommendation : ''}
                </p>
              </div>`,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    });
  }

  async proceedPayment(skipValidation = false) {
    if (!this.isFormValid() && !skipValidation) {
      this.validationTouched = {
        name: true,
        email: true,
        card: true,
        expDate: true,
        cvc: true,
        cardholderName: true,
      };
      alert('Complete datos personales y de pago.');
      return;
    }

    this.buttonStatus.checkout = false;

    // Show loading modal
    this.loadingComponent?.toggle('Procesando la orden...');

    const discountTokens: NonEmptyDiscountTokens | undefined =
      this.discountTokens.length > 0
        ? (this.discountTokens as NonEmptyDiscountTokens)
        : undefined;

    try {
      const isPhone = this.smartInputMode === 'phone';
      // Invisible reCAPTCHA v3 — runs on the pay click, no user interaction.
      const recaptchaToken = await firstValueFrom(
        this.recaptchaV3Service.execute('checkout')
      );
      const checkoutResponse = await this.checkoutSessionService.checkout({
        session_token: this.sessionToken,
        google_pay_token: this.googlePayToken,
        apple_pay_token: this.applePayToken,
        buyer_email: isPhone ? undefined : this.buyerData.email,
        buyer_phone: isPhone ? this.buyerData.email : undefined,
        buyer_full_name: this.buyerData.name,
        is_secured: this.paymentInfo.secured,
        promoter_slug: this.promoterSlug,
        discount_tokens: discountTokens,
        recaptchaToken,
        challengeToken: this.challengeToken ?? undefined,
        card_info: {
          cvc: this.paymentInfo.cvc,
          exp: this.paymentInfo.expDate,
          card: this.paymentInfo.card,
          type: this.paymentInfo.cardType,
          buyer_full_name: this.paymentInfo.name,
        },
      });
      this._onPaymentProceed(checkoutResponse);
    } catch (error: any) {
      this.handlePaymentError(error);
    }
  }

  // Solving the checkbox only unlocks the pay button — it must not charge the card
  // on its own. The widget stays up, and emits null when it expires, which clears
  // the token and locks the button again.
  onChallengeResolved(token: string | null) {
    this.challengeToken = token;
  }

  private handlePaymentError(error: any) {
    // Hide loading modal on error
    this.loadingComponent?.toggle();

    if (error?.status === 429) {
      this.buttonStatus.checkout = true;
      this.paymentStatus.next({ authorized: false });
      Swal.fire({
        title: 'Demasiados intentos',
        html: `
        <div>
          <p>Has hecho demasiados intentos de pago. Por favor espera un minuto e inténtalo de nuevo.</p>
        </div>`,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    const status = error.error?.error?.details
      ?.status as CybersourceThreeDSStatus;

    // Not a decline: the backend wants the checkbox solved before charging.
    if ((status as string) === 'CHALLENGE_REQUIRED') {
      this.challengeToken = null;
      // v2 tokens are single-use: if the widget is already up and showing a solved
      // checkbox, it has to be reset or there is no way to produce a fresh token.
      this.challengeBox?.reset();
      this.challengeRequired = true;
      this.buttonStatus.checkout = true;
      return;
    }

    if (!status) {
      this.paymentError('La orden no pudo ser completada');
      return;
    }

    let data!: { recommendation: string; response: string };
    switch (status) {
      case CybersourceThreeDSStatus.INVALID_REQUEST:
        data = Constants.CybersourcePaymentError(
          CybersourceThreeDSStatus.INVALID_CARD
        );
        break;
      default:
        data = Constants.CybersourcePaymentError(status);
        break;
    }

    this.paymentError(data);
  }

  /**
   * Validate checkout form
   * @return boolean
   */
  isFormValid(): boolean {
    // Basic validation - name and email must be filled
    const nameValid = this.buyerData.name.trim() !== '';

    let emailValid = true;
    if (this.smartInputMode === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      emailValid = emailPattern.test(this.buyerData.email.trim());
    }

    if (this.smartInputMode === 'phone') {
      emailValid = this.validatePhoneNumber(this.buyerData.email);
    }

    const basicValidation =
      nameValid && emailValid && this.buyerData.email.trim().length > 5;

    // For free events, only basic validation is needed
    if (this.eventFree) {
      return basicValidation;
    }

    // === Card validations ===
    const cardNamePattern = /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+$/; // Solo letras y espacios
    const cardNumberPattern = /^(\d{7}|\d{16})$/; // Exactamente 7 o 16 dígitos
    const cvcPattern = /^\d{3}$/; // 3 dígitos numéricos

    const cardNameValid = cardNamePattern.test(this.paymentInfo.name.trim());
    const cardValid = cardNumberPattern.test(
      this.paymentInfo.card.replace(/\s+/g, '')
    );
    const expDateValid = this.isExpirationDateValid();
    const cvcValid = cvcPattern.test(this.paymentInfo.cvc.trim());

    const paymentValidation =
      cardNameValid && cardValid && expDateValid && cvcValid;

    return basicValidation && paymentValidation;
  }

  // Individual validation methods for visual feedback
  isNameValid(): boolean {
    return this.buyerData.name.trim() !== '';
  }

  isEmailValid(): boolean {
    if (!this.buyerData.email || this.buyerData.email.trim().length <= 5) {
      return false;
    }

    if (this.smartInputMode === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(this.buyerData.email.trim());
    }

    if (this.smartInputMode === 'phone') {
      return this.validatePhoneNumber(this.buyerData.email);
    }

    return false;
  }

  isCardNumberValid(): boolean {
    if (this.eventFree) return true;
    const cardNumberPattern = /^(\d{7}|\d{16})$/;
    return cardNumberPattern.test(this.paymentInfo.card.replace(/\s+/g, ''));
  }

  isExpirationDateValid(): boolean {
    if (this.eventFree) return true;
    if (this.isExpirationDateExpired) return false;
    const expDatePattern = /^(0[1-9]|1[0-2])\d{2}$/; // Formato MMYY
    return expDatePattern.test(this.paymentInfo.expDate.trim());
  }

  isCVVValid(): boolean {
    if (this.eventFree) return true;
    const cvcPattern = /^\d{3}$/;
    return cvcPattern.test(this.paymentInfo.cvc.trim());
  }

  isCardholderNameValid(): boolean {
    if (this.eventFree) return true;
    const cardNamePattern = /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+$/;
    return cardNamePattern.test(this.paymentInfo.name.trim());
  }

  // Helper methods to check if field should show error
  shouldShowNameError(): boolean {
    return this.validationTouched.name && !this.isNameValid();
  }

  shouldShowEmailError(): boolean {
    return this.validationTouched.email && !this.isEmailValid();
  }

  shouldShowCardNumberError(): boolean {
    return this.validationTouched.card && !this.isCardNumberValid();
  }

  shouldShowExpirationDateError(): boolean {
    return this.validationTouched.expDate && !this.isExpirationDateValid();
  }

  shouldShowCVVError(): boolean {
    return this.validationTouched.cvc && !this.isCVVValid();
  }

  shouldShowCardholderNameError(): boolean {
    return (
      this.validationTouched.cardholderName && !this.isCardholderNameValid()
    );
  }

  private async handleCybersourceThreeDSSession(resp: any) {
    const { accessToken, deviceDataCollectionUrl, referenceId } =
      resp.consumerAuthenticationInformation;
    const onGettingResult = () => {
      this._hideLoadingDialog(() => {});
    };

    try {
      const data = await this.threedsCybersourceService.handleThreeDSSession({
        ddp: { accessToken, deviceDataCollectionUrl, referenceId },
        paymentHistoryId: resp.payment_history_id,
        onGettingResult,
      });
      return data;
    } catch (err) {
      console.log(err);
      return;
    }
  }

  private async _onPaymentProceed(resp: CheckoutResponse): Promise<void> {
    if ('consumerAuthenticationInformation' in resp) {
      const data = await this.handleCybersourceThreeDSSession(resp);

      if (!data) {
        this.paymentError('La orden no pudo ser completada');
        return;
      }

      if ('code' in data) {
        const { response, recommendation } =
          Constants.TransactionErrors[data.code];
        this.paymentError({ recommendation, response });
        return;
      }

      if (!data.order) {
        this.paymentError('La orden no pudo ser completada');
        return;
      }

      this.order = data.order;

      return this.handleEndSuccessPaymentSession(
        data.order.slug,
        data.pixelId,
        data.gtmId
      );
    }

    if (
      'ResponseMessage' in resp &&
      resp.ResponseMessage === '3D_SECURE_2_METHOD'
    ) {
      this.threeDSForm = this.sanitizer.bypassSecurityTrustHtml(
        resp.ThreeDSMethod.MethodForm
      );
      setTimeout(() => {
        const form = document.querySelector(
          '#tdsMmethodForm'
        ) as HTMLFormElement;
        form.submit();
      }, 300);
      const sid = resp.TermUrl.split('sid=')[1];
      const notificationListenerKey = `3ds_notification_${sid}`;
      this.utilService.getSocket().on(notificationListenerKey, (response) => {
        this.eventService
          .checkoutThreeds({
            data: {
              ...resp,
              notificationStatus: response.threeDSMethodData
                ? 'RECEIVED'
                : 'EXPECTED_BUT_NOT_RECEIVED',
            },
          })
          .subscribe({
            next: (resp) => this._onPaymentProceed(resp as CheckoutResponse),
            error: (err) => this.paymentError(err),
          });
        this.utilService.getSocket().removeListener(notificationListenerKey);
      });

      return;
    } else if (
      'ResponseMessage' in resp &&
      resp.ResponseMessage === '3D_SECURE_CHALLENGE'
    ) {
      const challengeData = resp.ThreeDSChallenge;

      this.threeChallengeModal.formConfig = {
        CReq: challengeData.CReq,
        TermUrl: resp.TermUrl,
        RedirectPostUrl: challengeData.RedirectPostUrl,
      };

      setTimeout(() => {
        this.loadingComponent.toggle();
        this.threeChallengeModal.threeDsChallengeBtn.nativeElement.click();
        this.threeChallengeModal.challengeForm.nativeElement.submit();
      }, 300);

      const sid = resp.TermUrl.split('sid=')[1];
      const notificationListenerKey = `3ds_notification_${sid}`;
      this.utilService.getSocket().on(notificationListenerKey, (response) => {
        this.loadingComponent.toggle();
        this.threeChallengeModal.threeDsChallengeBtn.nativeElement.click();
        this.eventService
          .checkoutThreeds({
            data: {
              ...resp,
              CRes: response.cres,
            },
          })
          .subscribe({
            next: (resp) =>
              this._onPaymentProceed(resp as CheckoutAzulResponse),
            error: (err) => this.paymentError(err),
          });
        this.utilService.getSocket().removeListener(notificationListenerKey);
      });
      return;
    }

    if (!('details' in resp)) {
      return this.paymentError(resp as CheckoutTixErrorResponse);
    }

    const newOrder = resp.data.order;
    resp.data.order;
    this.order = {
      buyerName: newOrder.buyer_full_name,
      buyerEmail: newOrder.buyer_email,
      totalToPay: newOrder.total,
      subTotal: newOrder.sub_total,
      serviceFee: newOrder.service_fee,
      insuranceFee: newOrder.insurance_fee,
      id: newOrder.id,
      createdAt: new Date(newOrder.created_at),
      slug: newOrder.slug,
    } as Order;

    return this.handleEndSuccessPaymentSession(
      newOrder.slug,
      resp.data.pixel_id,
      resp.data.gtm_id
    );
  }

  private handleEndSuccessPaymentSession(
    slug: string,
    pixelId?: number,
    gtmId?: string
  ) {
    this.paymentStatus.next({ authorized: true });
    this._hideLoadingDialog(() => {
      if (this.countDownInterval != null) {
        clearInterval(this.countDownInterval as any);
        this.countDownInterval = null;
      }
      if (this.order) {
        this.order.totalToPay = this.paymentInfo.secured
          ? this.orderPrice.securedTotal
          : this.orderPrice.total;

        this.order.subTotal = this.orderPrice.orderAmount;
        this.order.serviceFee = this.orderPrice.fees.serviceFee;
        this.order.insuranceFee = this.paymentInfo.secured
          ? this.orderPrice.fees.insuranceFee
          : 0;
      }

      this.storageService.set(
        Constants.Keys.OrderConfirmation,
        JSON.stringify(this.order)
      );

      this._clearSession(false);

      this.router.navigate([`/order/${slug}/confirmation`], {
        replaceUrl: true,
        queryParams: { pixelId, gtmId },
      });
    });
  }

  /**
   * Hides the loading dialog.
   * This expression was put inside a particular method because a bug when the dialog is request to be closed.
   * It seems that if both events (show and hide) occur within a small difference of time, dismiss doesn't work
   * properly.
   *
   * The call to toggle function is wrapped inside a setTimeout call that takes 1 second to execute.
   * @param callback
   * @return void
   * */
  private _hideLoadingDialog(callback: Function): void {
    //TODO: Behavior bug should be fixed.
    if (this.loadingComponent?.isVisible()) {
      setTimeout(() => {
        this.loadingComponent?.toggle();
        setTimeout(() => {
          callback();
        }, 500);
      }, 500);
    } else {
      callback();
    }
  }

  private _startTimer(reset = false): void {
    this.timeoutMinutes = 0;
    this.timeoutSeconds = 0;

    this.countDownInterval = this.utilService.startTimer((secLefts) => {
      this.timeoutMinutes = parseInt((secLefts / 60).toString());
      this.timeoutSeconds = secLefts - this.timeoutMinutes * 60;

      if (secLefts <= 0) {
        this._onTimerLimitReached();
      }
    });
  }

  private _onTimerLimitReached(): void {
    this.checkoutSessionService.endSession(this.sessionToken, true);
    clearInterval(this.countDownInterval as any);
    sessionStorage.removeItem(Constants.Keys.CheckoutSession);
    alert('Tiempo de espera para procesar orden excedido.');
    this.goBackToEvent();
  }

  handleCouponCodeChange($event: Event) {
    const target = $event.target as HTMLInputElement;

    this.couponCode = target.value.toUpperCase();
  }

  toggleSummary() {
    this.showSummary = !this.showSummary;
  }

  async removeCardDiscount() {
    this.discountTokens = [];
    this.cardCoupon = undefined;
    await this.applyPrices();
    if (!(this.order && this.order.coupons)) return;

    const i = this.order.coupons.findIndex((c) => c.id == this.cardCoupon?.id);

    if (i == -1) return;

    this.order.coupons.splice(i, 1);
    this.cardCoupon = undefined;
  }

  onLoadPaymentData(data: LoadPaymentDataInfo) {
    if (data.wallet === 'google') {
      const detail = data.data as GooglePayResponse;
      const name = detail.paymentMethodData.info.billingAddress.name;

      if (!this.buyerData.name) {
        this.buyerData.name = name;
      }

      if (!this.buyerData.email) {
        this.buyerData.email = detail.email;
      }

      this.emailInput.setValue(this.buyerData.email);
      const token = detail.paymentMethodData.tokenizationData.token;
      this.googlePayToken = token;
      this.paymentInfo.cardType =
        detail.paymentMethodData.info.cardNetwork.toLowerCase();
      this.paymentInfo.card = detail.paymentMethodData.info.cardDetails;
      const skipValidations = true;
      this.proceedPayment(skipValidations);
    } else if (data.wallet === 'apple') {
      const json = {
        paymentMethod: {
          network: data.data.token.payment.token.paymentMethod.network,
          type: data.data.token.payment.token.paymentMethod.type,
          displayName: data.data.token.payment.token.paymentMethod.displayName,
        },
        paymentData: {
          data: data.data.token.payment.token.paymentData.data,
          signature: data.data.token.payment.token.paymentData.signature,
          header: {
            publicKeyHash:
              data.data.token.payment.token.paymentData.header.publicKeyHash,
            ephemeralPublicKey:
              data.data.token.payment.token.paymentData.header
                .ephemeralPublicKey,
            transactionId:
              data.data.token.payment.token.paymentData.header.transactionId,
          },
          version: data.data.token.payment.token.paymentData.version,
        },
        transactionIdentifier:
          data.data.token.payment.token.transactionIdentifier,
      };

      this.applePayToken = JSON.stringify(json) as any;
      this.paymentInfo.cardType =
        data.data.token.payment.token.paymentMethod.network.toLowerCase();

      const contact = data.data.token.payment.shippingContact;
      this.paymentInfo.card =
        data.data.token.payment.token.paymentMethod.displayName.split(' ')[1];

      if (!this.buyerData.name) {
        this.buyerData.name = contact.givenName + ' ' + contact.familyName;
      }

      if (!this.buyerData.email) {
        this.buyerData.email = contact.emailAddress;
      }

      this.proceedPayment(true);
    }

    this.detectedCardBrand = this.paymentInfo.cardType.toLowerCase() as
      | 'visa'
      | 'mastercard'
      | 'amex';
  }

  private escapeJSON(obj: object) {
    const str = JSON.stringify(obj);
    return str
      .replace(/[\\]/g, '\\\\')
      .replace(/[\"]/g, '\\"')
      .replace(/[\/]/g, '\\/')
      .replace(/[\b]/g, '\\b')
      .replace(/[\f]/g, '\\f')
      .replace(/[\n]/g, '\\n')
      .replace(/[\r]/g, '\\r')
      .replace(/[\t]/g, '\\t');
  }

  // Called on input change for the card field
  public onCardInput(value: string) {
    const digitsOnly = (value || '').replace(/\D/g, '');

    // Check if first digit is 3 (AMEX) and show warning
    if (digitsOnly && digitsOnly.charAt(0) === '3') {
      this.detectedCardBrand = 'amex';
      this.paymentInfo.cardType = 'amex';
      // Clear the input to prevent AMEX from being entered
      this.paymentInfo.card = '';
      return;
    }

    const brand = this.detectCardBrand(digitsOnly);
    this.detectedCardBrand = brand;
    this.paymentInfo.cardType = brand ?? '';
    this.validationTouched.card = true;

    // Auto-focus to expiration date when card is complete (16 digits)
    if (digitsOnly.length === 16) {
      setTimeout(() => {
        this.focusExpirationDate();
      }, 100);
    }
  }

  // Called on input change for the expiration date field
  public onExpDateInput(value: string) {
    // Validate if the date is expired
    this.validateExpirationDate(value);
    this.validationTouched.expDate = true;
  }

  // Called on keyup for the expiration date field (for better auto-navigation)
  public onExpDateKeyUp(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Auto-focus to CVV when expiration date is complete (MM/YY format = 5 characters)
    if (value && value.length === 5) {
      setTimeout(() => {
        this.focusCVV();
      }, 100);
    }

    // Also validate the date on keyup for immediate feedback
    this.validateExpirationDate(value);
  }

  // Called on input change for the CVV field
  public onCVVInput(value: string) {
    this.validationTouched.cvc = true;
    // Auto-focus to cardholder name when CVV is complete (3 digits for all cards)
    if (value && value.length === 3) {
      setTimeout(() => {
        this.focusCardholderName();
      }, 100);
    }
  }

  // Called on input change for the cardholder name field
  public onCardholderNameInput(value: string) {
    // This field is the last one, so no auto-focus needed
    //console.log('Cardholder name input:', value);
    this.validationTouched.cardholderName = true;
  }

  // Mark fields as touched when user interacts
  onNameInput(value: string) {
    this.buyerData.name = value;
    this.validationTouched.name = true;
  }

  onEmailInputTouched() {
    this.validationTouched.email = true;
  }

  // Handle free tickets reception
  public receiveFreeTickets(): void {
    // Validate form before proceeding
    if (!this.isFormValid()) {
      return;
    }

    // Proceed with free ticket processing
    this.proceedPayment(true); // Skip payment validations for free events
  }

  // Validate expiration date
  private validateExpirationDate(value: string) {
    if (value && value.length === 5) {
      const [month, year] = value.split('/');
      const expMonth = parseInt(month);
      const expYear = parseInt(year);

      // Validate month range (1-12)
      if (expMonth < 1 || expMonth > 12) {
        this.isExpirationDateExpired = true;
        return;
      }

      // Get current date
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
      const currentMonth = currentDate.getMonth() + 1; // January is 0

      // Check if date is expired
      this.isExpirationDateExpired =
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth);
    } else {
      this.isExpirationDateExpired = false;
    }
  }

  private detectCardBrand(
    digits: string
  ): 'visa' | 'mastercard' | 'amex' | 'discover' | null {
    if (!digits) return null;

    // American Express: starts with 34 or 37
    if (/^3[47]/.test(digits)) return 'amex';

    // Visa: starts with 4
    if (/^4/.test(digits)) return 'visa';

    // Mastercard: starts with 5
    if (/^5/.test(digits)) return 'mastercard';

    // Discover: starts with 6
    if (/^6/.test(digits)) return 'discover';

    return null;
  }

  async searchCardDiscounts() {
    if (this.paymentInfo.card.length !== 16) return;

    try {
      let coupon = await lastValueFrom(
        this.couponService.find(
          this.paymentInfo.card.slice(0, 6),
          this.event.id,
          [],
          CouponType.CreditCard
        )
      );

      if (typeof coupon == 'string' || !coupon) {
        this.cardCoupon && (await this.removeCardDiscount());
        return;
      } else {
        await this.applyPrices(coupon.discountToken);
      }

      this.cardCoupon;
      if (this.cardCoupon) {
        this.removeCardDiscount();
      }

      this.cardCoupon = coupon as ExplicitCouponType<CouponType.CreditCard>;
      this.discountTokens.push(coupon.discountToken);

      await Swal.fire({
        html: `Descuento (${coupon.name} -${coupon.discount}%) aplicado correctamente`,
        icon: 'success',
      });
    } catch (e) {
      console.log(e);
    }
  }

  private addStartOnDates(tts: TicketType[]) {
    this.dates = [];

    for (const tt of tts) {
      if (tt.start_on !== null && !this.dates.includes(tt.start_on)) {
        this.dates.push(tt.start_on);
      }
    }

    if (this.dates.length === 0 && this.event) {
      this.dates.push(this.event.startAt.toString());
    }

    this.dates.sort();
  }

  private goBackToEvent() {
    return this.router.navigate(
      [`event/${this.route.snapshot.params['slug']}`],
      { replaceUrl: true }
    );
  }

  private async getPricesWithRetries(
    tries: number,
    products: Product[],
    discountTokens: string[]
  ) {
    while (tries > 0) {
      const resp = await this.getPrices(products, discountTokens);

      if (resp) {
        return resp;
      }

      tries--;
    }
    return null;
  }

  private getPrices(
    products: Product[],
    discountTokens: string[]
  ): Promise<OrderPrices | null> {
    return new Promise((resolve, reject) => {
      try {
        this.eventService
          .getOrderPrices(products, discountTokens)
          .subscribe((resp) => {
            resolve(resp);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  private async applyPrices(discountToken?: string) {
    if (discountToken) {
      this.discountTokens.push(discountToken);
    }

    const orderFee = await this.getPricesWithRetries(
      Constants.RetriesN.CalcFees,
      this.products,
      this.discountTokens
    );

    if (!orderFee) {
      await this.goBackToEvent();
      return;
    }

    this.orderPrice = orderFee;
    this.eventFree = orderFee.total === 0;
    this.gateway = orderFee.gateway?.toUpperCase() || 'AZUL';
    this.insuranceFee = orderFee.fees.insuranceFee;

    if (!this.paymentInfo.secured) {
      this.orderPrice.fees.insuranceFee = 0;
    }

    this.calcTotal();
  }

  // Auto-navigation functions
  private focusExpirationDate() {
    const expDateInput = document.querySelector(
      'input[name="expDate"]'
    ) as HTMLInputElement;
    if (expDateInput) {
      expDateInput.focus();
    }
  }

  private focusCVV() {
    const cvvInput = document.querySelector(
      'input[name="cvc"]'
    ) as HTMLInputElement;
    if (cvvInput) {
      cvvInput.focus();
    }
  }

  private focusCardholderName() {
    const nameInput = document.querySelector(
      'input[name="cardholderName"]'
    ) as HTMLInputElement;
    if (nameInput) {
      nameInput.focus();
    }
  }

  // Mobile-specific auto-navigation functions
  public onCardInputMobile(event: any) {
    const value = event.target.value;
    const digitsOnly = (value || '').replace(/\D/g, '');

    // Auto-focus to expiration date when card is complete (16 digits)
    if (digitsOnly.length === 16) {
      setTimeout(() => {
        this.focusExpirationDateMobile();
      }, 100);
    }
  }

  public onExpDateInputMobile(event: any) {
    const value = event.target.value;

    // Auto-focus to CVV when expiration date is complete (MM/YY format = 5 characters)
    if (value && value.length === 5) {
      setTimeout(() => {
        this.focusCVVMobile();
      }, 100);
    }
  }

  public onCVVInputMobile(event: any) {
    const value = event.target.value;

    // Auto-focus to cardholder name when CVV is complete (3 digits)
    if (value && value.length === 3) {
      setTimeout(() => {
        this.focusCardholderName();
      }, 100);
    }
  }

  private focusExpirationDateMobile() {
    const expDateInput = document.querySelector(
      'input[name="expDateMobile"]'
    ) as HTMLInputElement;
    if (expDateInput) {
      expDateInput.focus();
    }
  }

  private focusCVVMobile() {
    const cvvInput = document.querySelector(
      'input[name="cvcMobile"]'
    ) as HTMLInputElement;
    if (cvvInput) {
      cvvInput.focus();
    }
  }

  // ============================================================================
  // TOOLTIP POSITIONING FOR MOBILE
  // ============================================================================

  /**
   * Ajusta la posición del tooltip automáticamente en mobile
   * para evitar que se corte en los bordes de la pantalla
   */
  adjustTooltipPosition(event: MouseEvent, tooltipElement: HTMLElement): void {
    if (window.innerWidth <= 768) {
      // Forzar el cálculo del layout antes de obtener las dimensiones
      tooltipElement.style.visibility = 'hidden';
      tooltipElement.style.display = 'flex';

      const rect = tooltipElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Verificar si es el tooltip de pago (el que contiene "Pago seguro con")
      const isPaymentTooltip =
        tooltipElement.textContent?.includes('Pago seguro con');

      if (isPaymentTooltip) {
        // Para el tooltip de pago, forzar que aparezca debajo
        tooltipElement.classList.add('tooltip-bubble--below');
        tooltipElement.classList.remove('tooltip-bubble--above');

        // Posicionar centrado horizontalmente
        tooltipElement.style.left = '50%';
        tooltipElement.style.transform = 'translateX(-50%)';
      } else {
        // Para otros tooltips, mantener la lógica original
        // Calcular el espacio disponible
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;
        const tooltipHeight = rect.height;

        // Determinar la mejor posición vertical
        if (spaceAbove >= tooltipHeight + 50) {
          // Hay espacio arriba, mostrar arriba
          tooltipElement.classList.add('tooltip-bubble--above');
          tooltipElement.classList.remove('tooltip-bubble--below');
        } else if (spaceBelow >= tooltipHeight + 50) {
          // Hay espacio abajo, mostrar abajo
          tooltipElement.classList.add('tooltip-bubble--below');
          tooltipElement.classList.remove('tooltip-bubble--above');
        } else {
          // No hay espacio suficiente, mostrar arriba por defecto
          tooltipElement.classList.add('tooltip-bubble--above');
          tooltipElement.classList.remove('tooltip-bubble--below');
        }

        // Ajustar posición horizontal
        const tooltipWidth = rect.width;
        let leftPosition = 50;

        if (tooltipWidth > viewportWidth - 40) {
          // Tooltip muy ancho, centrar
          leftPosition = 50;
          tooltipElement.style.left = '50%';
          tooltipElement.style.transform = 'translateX(-50%)';
        } else {
          // Calcular la mejor posición horizontal
          const centerX = viewportWidth / 2;
          const tooltipCenter = rect.left + tooltipWidth / 2;

          if (tooltipCenter < centerX) {
            // Tooltip está a la izquierda, alinear a la izquierda
            leftPosition = Math.max(20, (rect.left / viewportWidth) * 100);
            tooltipElement.style.left = `${leftPosition}%`;
            tooltipElement.style.transform = 'none';
          } else {
            // Tooltip está a la derecha, alinear a la derecha
            leftPosition = Math.min(
              80,
              ((rect.right - tooltipWidth) / viewportWidth) * 100
            );
            tooltipElement.style.left = `${leftPosition}%`;
            tooltipElement.style.transform = 'none';
          }
        }
      }

      // Asegurar que el tooltip esté visible
      tooltipElement.style.zIndex = '9999';
      tooltipElement.style.visibility = 'visible';
    }
  }

  /**
   * Muestra el tooltip con la posición correcta
   */
  showTooltip(event: MouseEvent, tooltipClass: string): void {
    const tooltip = (event.currentTarget as HTMLElement)?.querySelector(
      `.${tooltipClass}`
    ) as HTMLElement;
    if (tooltip) {
      // Mostrar el tooltip
      tooltip.style.display = 'flex';
      tooltip.style.opacity = '1';

      // Delay para asegurar que el tooltip esté visible antes de ajustar
      setTimeout(() => {
        this.adjustTooltipPosition(event, tooltip);
      }, 50);
    }
  }

  /**
   * Muestra el tooltip para eventos táctiles
   */
  showTooltipTouch(event: TouchEvent, tooltipClass: string): void {
    const tooltip = (event.currentTarget as HTMLElement)?.querySelector(
      `.${tooltipClass}`
    ) as HTMLElement;
    if (tooltip) {
      // Mostrar el tooltip
      tooltip.style.display = 'flex';
      tooltip.style.opacity = '1';

      // Crear un evento mouse simulado para el posicionamiento
      const simulatedMouseEvent = {
        currentTarget: event.currentTarget,
        clientX: event.touches[0]?.clientX || 0,
        clientY: event.touches[0]?.clientY || 0,
      } as MouseEvent;

      // Delay para asegurar que el tooltip esté visible antes de ajustar
      setTimeout(() => {
        this.adjustTooltipPosition(simulatedMouseEvent, tooltip);
      }, 50);
    }
  }

  /**
   * Oculta el tooltip
   */
  hideTooltip(event: MouseEvent, tooltipClass: string): void {
    const tooltip = (event.currentTarget as HTMLElement)?.querySelector(
      `.${tooltipClass}`
    ) as HTMLElement;
    if (tooltip) {
      // Ocultar el tooltip
      tooltip.style.display = 'none';
      tooltip.style.opacity = '0';

      // Limpiar clases de posición
      tooltip.classList.remove(
        'tooltip-bubble--above',
        'tooltip-bubble--below'
      );
      // Limpiar estilos inline
      tooltip.style.left = '';
      tooltip.style.right = '';
      tooltip.style.transform = '';
    }
  }

  /**
   * Oculta el tooltip para eventos táctiles
   */
  hideTooltipTouch(event: TouchEvent, tooltipClass: string): void {
    const tooltip = (event.currentTarget as HTMLElement)?.querySelector(
      `.${tooltipClass}`
    ) as HTMLElement;
    if (tooltip) {
      // Ocultar el tooltip
      tooltip.style.display = 'none';
      tooltip.style.opacity = '0';

      // Limpiar clases de posición
      tooltip.classList.remove(
        'tooltip-bubble--above',
        'tooltip-bubble--below'
      );
      // Limpiar estilos inline
      tooltip.style.left = '';
      tooltip.style.right = '';
      tooltip.style.transform = '';
    }
  }

  smartInputMode: string = 'email';

  onContactoChange(value: any): void {
    //console.log('Valor input:', value);
    this.buyerData.email = value || '';
    this.validationTouched.email = true;
  }

  onModeChange(mode: any): void {
    //console.log('Modo:', mode);
    this.smartInputMode = mode;
  }

  private validatePhoneNumber(phone: string): boolean {
    const country = this.detectCountryFromPhone(phone);
    if (!country) return false;

    const digitsOnly = phone.replace(/\D/g, '');
    const dialDigits = country.dialCode.replace(/\D/g, '');
    const remainingLength = digitsOnly.slice(dialDigits.length).length;
    const lengthValid = country.maxLength
      ? remainingLength === country.maxLength
      : true;

    return lengthValid;
  }

  private detectCountryFromPhone(phone: string): Country | null {
    const digitsOnly = phone.replace(/\D/g, '');

    // Ordenamos de mayor a menor longitud del dialCode para evitar conflictos (ej: +1 vs +52)
    const sortedCountries = [...COUNTRIES].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );

    for (const country of sortedCountries) {
      const dialDigits = country.dialCode.replace(/\D/g, '');
      if (digitsOnly.startsWith(dialDigits)) {
        return country;
      }
    }

    return null;
  }
}
